/**
 * Navigations-Guards.
 *
 * `resolveNavigation` ist bewusst rein (keine Store-/Router-Zugriffe), damit
 * die Regeln direkt testbar sind; `installGuards` verdrahtet sie mit Store,
 * Router und Dokumenttitel.
 */
import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'

import { toast } from '@/composables/toast'
import { de } from '@/strings/de'
import { useAuthStore } from '@/stores/auth'

export interface AuthSnapshot {
  isAuthenticated: boolean
  isAdmin: boolean
}

/** Entscheidet, ob navigiert werden darf – oder wohin stattdessen. */
export function resolveNavigation(
  to: RouteLocationNormalized,
  auth: AuthSnapshot,
): true | RouteLocationRaw {
  const meta = to.meta

  // Angemeldete gehören nicht auf die Anmeldeseite.
  if (meta.guestOnly && auth.isAuthenticated) {
    return { name: 'patients' }
  }

  if (meta.public) return true

  if (!auth.isAuthenticated) {
    const redirect = to.fullPath
    return {
      name: 'signin',
      query: redirect && redirect !== '/' ? { redirect } : {},
    }
  }

  if (meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'patients' }
  }

  return true
}

/** Dokumenttitel: "<Seite> – SELWA für Therapeuten". */
export function documentTitle(to: RouteLocationNormalized): string {
  const title = to.meta.title
  return title ? `${title} – ${de.app.titleSuffix}` : de.app.title
}

export function installGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    // `/auth/me` genau einmal auflösen. Rein öffentliche Routen brauchen es
    // nicht; die Anmeldeseite schon, sonst greift `guestOnly` nicht.
    if ((!to.meta.public || to.meta.guestOnly) && !auth.resolved) {
      await auth.ensureResolved()
    }

    const snapshot: AuthSnapshot = {
      isAuthenticated: auth.isAuthenticated,
      isAdmin: auth.isAdmin,
    }

    const decision = resolveNavigation(to, snapshot)
    if (decision !== true && to.meta.requiresAdmin && snapshot.isAuthenticated) {
      toast.error(de.admin.forbidden)
    }
    return decision
  })

  router.afterEach((to) => {
    document.title = documentTitle(to)
  })
}
